import React from 'react';

const keyframes = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Vertical / small screens: stack the text and photo, centre everything,
     and stop the name from pushing the layout sideways. */
  @media (max-width: 768px) {
    .hero-section {
      padding: 110px 20px 70px !important;
      min-height: auto !important;
      align-items: flex-start !important;
    }
    .hero-inner {
      grid-template-columns: 1fr !important;
      gap: 44px !important;
      max-width: 480px !important;
    }
    .hero-left {
      align-items: center !important;
      text-align: center !important;
    }
    .hero-name { font-size: clamp(2.7rem, 13vw, 4rem) !important; }
    .hero-tagline {
      align-items: center !important;
      font-size: 1.15rem !important;
      letter-spacing: 0.12em !important;
      margin-top: 24px !important;
    }
    .hero-photo {
      width: 100% !important;
      max-width: 340px !important;
      margin: 0 auto !important;
    }
    .hero-scroll { display: none !important; }
  }
`;

const styles = {
  hero: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    padding: '0 80px',
    position: 'relative',
    overflow: 'hidden',
  },
  bg: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1510 50%, #0a0a0a 100%)',
    zIndex: 0,
  },
  grain: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
    zIndex: 1,
    opacity: 0.6,
  },
  accent: {
    position: 'absolute',
    top: '20%',
    right: '10%',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(201,169,110,0.06) 0%, transparent 70%)',
    zIndex: 1,
  },
  inner: {
    position: 'relative',
    zIndex: 2,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1300px',
    margin: '0 auto',
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  eyebrow: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.7rem',
    letterSpacing: '0.35em',
    textTransform: 'uppercase',
    color: '#c9a96e',
    marginBottom: '20px',
    opacity: 0,
    animation: 'fadeUp 0.8s ease 0.2s forwards',
  },
  name: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'clamp(3.5rem, 7vw, 8rem)',
    fontWeight: 300,
    lineHeight: 0.9,
    letterSpacing: '-0.01em',
    color: '#f5f2ee',
    opacity: 0,
    animation: 'fadeUp 0.8s ease 0.4s forwards',
  },
  nameItalic: {
    fontStyle: 'italic',
    color: '#c9a96e',
  },
  tagline: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '1.85rem',
    letterSpacing: '0.15em',
    color: '#6b6560',
    marginTop: '32px',
    opacity: 0,
    animation: 'fadeUp 0.8s ease 0.7s forwards',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  right: {
    opacity: 0,
    animation: 'fadeUp 0.8s ease 0.5s forwards',
  },
  imgWrapper: {
    aspectRatio: '2/3',
    background: 'linear-gradient(160deg, #1a1510, #0f0d0a)',
    border: '1px solid rgba(201,169,110,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  img: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  corner: {
    position: 'absolute',
    width: '20px',
    height: '20px',
    borderColor: '#c9a96e',
    borderStyle: 'solid',
    borderWidth: 0,
    opacity: 0.4,
  },
  scrollHint: {
    position: 'absolute',
    bottom: '40px',
    right: '60px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    zIndex: 2,
    opacity: 0,
    animation: 'fadeUp 0.8s ease 1.2s forwards',
  },
  scrollLine: {
    width: '1px',
    height: '60px',
    background: 'linear-gradient(to bottom, transparent, #c9a96e)',
  },
  scrollText: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.6rem',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#6b6560',
    writingMode: 'vertical-rl',
  },
};

export default function Hero() {
  return (
    <>
      <style>{keyframes}</style>
      <section className="hero-section" style={styles.hero}>
        <div style={styles.bg} />
        <div style={styles.grain} />
        <div style={styles.accent} />

        <div className="hero-inner" style={styles.inner}>
          {/* Left — text */}
          <div className="hero-left" style={styles.left}>
            <h1 className="hero-name" style={styles.name}>
              Noah<br />
              <span style={styles.nameItalic}>Sato</span>
            </h1>
            <div className="hero-tagline" style={styles.tagline}>
              <span>Austin, TX</span>
              <span>5'10'' (1.9m)</span>
              <span>200 lbs (90kg)</span>
              <span>28 Years</span>
              <span>American / Japanese</span>
              <span>Bilingual EN / JP</span>
            </div>
          </div>

          {/* Right — photo */}
          <div style={styles.right}>
            <div className="hero-photo" style={styles.imgWrapper}>
              {/* Headshot — generated from '16.jpg' in D:\ACTING PORTFOLIO\PICTURES\FINAL VERSIONS */}
              <img
                src="/images/headshot.jpg"
                alt="Noah Sato, actor"
                style={styles.img}
                width="1200"
                height="1800"
                loading="eager"
              />
              <div style={{ ...styles.corner, top: 16, left: 16, borderTopWidth: 1, borderLeftWidth: 1 }} />
              <div style={{ ...styles.corner, top: 16, right: 16, borderTopWidth: 1, borderRightWidth: 1 }} />
              <div style={{ ...styles.corner, bottom: 16, left: 16, borderBottomWidth: 1, borderLeftWidth: 1 }} />
              <div style={{ ...styles.corner, bottom: 16, right: 16, borderBottomWidth: 1, borderRightWidth: 1 }} />
            </div>
          </div>
        </div>

        <div className="hero-scroll" style={styles.scrollHint}>
          <span style={styles.scrollText}>Scroll</span>
          <div style={styles.scrollLine} />
        </div>
      </section>
    </>
  );
}
